import axios from 'axios'

let target = null
const configured = new WeakSet()
const retiredPages = new Set()
let observer = null
let navigationInstalled = false

export function normalizeIds(values) {
    return [...new Set((Array.isArray(values) ? values : []).map(String).filter(value => /^[1-9][0-9]*$/.test(value)))]
}

export function syncModatechState(widget, store) {
    widget.setCartProducts(normalizeIds(store.getters.counterBasketData?.ids), '/basket/')
    widget.setFavoritesProducts(normalizeIds(store.getters.counterFavoriteData?.ids))
}

function removeOwned(id) {
    document.querySelectorAll('[data-container-id="' + id + '"]').forEach(node => node.remove())
}

export function cleanupModatechPage(id, retire = true) {
    if (retire) {
        retiredPages.add(id)
        if (target?.id === id) { target = null }
        removeOwned(id)
    }
    if (!observer) {
        observer = new MutationObserver(() => retiredPages.forEach(removeOwned))
        observer.observe(document.body, {childList: true, subtree: true})
    }
    // The observer is shared for this app lifetime to catch late SDK responses.
    return () => { retiredPages.add(id); removeOwned(id) }
}

async function navigate(value) {
    const current = target
    if (!current || typeof value !== 'string') { return }
    const url = new URL(value, axios.defaults.baseURL)
    const allowed = [window.location.host, new URL(axios.defaults.baseURL).host, 'mcbuy.ru', 'www.mcbuy.ru']
    if (!['https:', 'http:'].includes(url.protocol) || !allowed.includes(url.host)) { return }
    let path
    if (/^\/(lookbooks|basket|favorite)\/$/.test(url.pathname) || /^\/detail\/[1-9][0-9]*\/$/.test(url.pathname)) {
        path = url.pathname + url.search
    } else {
        const response = await axios.get('api/modatech/product/', {params: {url: url.href}})
        if (response.data?.status !== 'success' || !response.data.data?.id) { throw new Error('Товар не найден') }
        path = '/detail/' + response.data.data.id + '/'
    }
    if (target !== current) { return }
    current.widget.closePopup?.()
    await current.router.push(path)
}

export function configureModatechActions(widget, store, router, showMessage, id, productLinksContainer = null) {
    target = {widget, store, router, showMessage, id, productLinksContainer}
    if (!navigationInstalled) {
        navigationInstalled = true
        // Some SDK links navigate directly even with spa:true. The product-page
        // "complete your look" slot lives outside the main widget container.
        document.addEventListener('click', event => {
            const control = event.target.closest?.('[data-link], a[href]')
            const current = target
            if (!control || !current) { return }
            const scope = control.closest('[data-container-id]') || control.closest('[id^="mcbuy-modatech-"]')
            const owner = scope?.getAttribute('data-container-id') || scope?.id
            if (owner !== current.id && !current.productLinksContainer?.contains(control)) { return }
            const href = control.getAttribute('data-link') || control.getAttribute('href')
            let url
            try { url = new URL(href, axios.defaults.baseURL) } catch (error) { return }
            const allowed = [window.location.host, new URL(axios.defaults.baseURL).host, 'mcbuy.ru', 'www.mcbuy.ru']
            if (!['https:', 'http:'].includes(url.protocol) || !allowed.includes(url.host)) { return }
            if (url.pathname !== '/basket/' && !url.pathname.startsWith('/catalog/')
                && !/^\/detail\/[1-9][0-9]*\/$/.test(url.pathname)) { return }
            event.preventDefault()
            event.stopImmediatePropagation()
            navigate(url.href).catch(() => {
                if (target === current) { current.showMessage('Не удалось открыть товар. Попробуйте ещё раз.') }
            })
        }, true)
    }
    if (configured.has(widget)) { return }
    configured.add(widget)
    const handle = async (action, success) => {
        const current = target
        if (!current) { return }
        try {
            const result = await action(current)
            if (!result?.success) { throw new Error(result?.message || 'Не удалось выполнить действие. Попробуйте ещё раз.') }
            if (target === current) {
                syncModatechState(widget, store)
                current.showMessage(success)
            }
        } catch (error) {
            if (target === current) {
                // Restore SDK's optimistic state even when the server rejected the action.
                try { syncModatechState(widget, store) } catch (ignored) { /* SDK failure */ }
                current.showMessage(error.message || 'Не удалось выполнить действие. Попробуйте ещё раз.')
            }
        }
    }
    widget.registerCallback.addToCartEvent((offerId) => {
        if (!normalizeIds([offerId]).length) { return }
        handle(async current => {
            const result = await current.store.dispatch('addToBasket', {productId: Number(offerId), quantity: 1})
            if (result?.success && current.router.currentRoute.value.name === 'basket') {
                await current.store.dispatch('fetchBasket', true)
            }
            return result
        }, 'Товар добавлен в корзину')
    })
    widget.registerCallback.addToFavorites((productId, state) => {
        if (!normalizeIds([productId]).length || typeof state !== 'boolean') { return }
        handle(async current => {
            const result = await current.store.dispatch('setFavoriteState', {productId: Number(productId), state})
            return result
        }, state ? 'Товар добавлен в избранное' : 'Товар удалён из избранного')
    })
    widget.registerCallback.linkClicked?.(link => {
        navigate(link).catch(() => target?.showMessage('Не удалось открыть товар. Попробуйте ещё раз.'))
    })
}
